import debounce from "lodash/debounce";
import throttle from "lodash/throttle";

import * as analytics from "./util/segment";

const EMAIL_REGEX = new RegExp(/^([\w\-\.]+)@([\w\-\.]+)\.([a-zA-Z]{2,})$/);
const MOBILE_BREAKPOINT = 500; // Must remain in sync with extra-small breakpoint in constants.scss
const POPUP_APPEAR_TIMEOUT = 60 * 1000; // 60 seconds
const POPUP_TRANSITION = 400 // Must remain in sync with the transition time for feedback popup class in article-feedback.scss
const MIN_MESSAGE_LENGTH = 5;
const FORM_ENDPOINT = "https://www.singlestore.com/api/v1/docs-feedback/";

const CONTAINER_TYPES = [
    "article-end",
    "popup",
    "modal",
];

const getViewport = () => Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
);

export default class ArticleFeedback {
    createSelector($selectorContext) {
        return $selector => {
            if ($selector) {
                return $($selector, $($selectorContext));
            }
            return $($selectorContext);
        };
    }

    createToggle($selector) {
        return (show, speed) => {
            if (show) {
                $selector.addClass("show");
                if (speed) {
                    $selector.fadeIn(speed);
                } else {
                    $selector.show();
                }
            } else {
                $selector.removeClass("show");
                if (speed) {
                    $selector.fadeOut(speed);
                } else {
                    $selector.hide();
                }
            }
        };
    }

    /**
     * @function InViewport
     * @description Returns true when the element is within the viewport bounds,
     * even if the element is only partially visible
     */
    isInViewport($selector) {
        const elementTop = $selector.offset().top;
        const elementBottom = elementTop + $selector.outerHeight();
    
        const viewportTop = $(window).scrollTop();
        const viewportBottom = viewportTop + $(window).height();
    
        return elementBottom > viewportTop && elementTop < viewportBottom;
    }

    getContainerType($selector) {
        const parents = $selector.parents(".feedback-container");
        if (parents.length) {
            const container = $(parents[0]);
            return CONTAINER_TYPES.find(type =>
                container.hasClass(`feedback-${type}`)
            );
        }
        return undefined;
    }

    trackFeedback(action) {
        analytics.track({
            action: `article-${action}`,
            category: "article-feedback",
        });
    }

    validateEmail(email) {
        return email.length
            ? email.length && EMAIL_REGEX.test(email)
            : true;
    }

    validateMessage(message) {
        return message.length >= MIN_MESSAGE_LENGTH;
    }

    mount({ feedbackPopupId, feedbackModalId, feedbackArticleEndId }) {
        let isDismissed = false;
        let hasPopupBeenSeen = false;
        let viewportWidth = getViewport();

        const updateViewport = debounce(() => {
            viewportWidth = getViewport();
        });

        $(window).on("resize", updateViewport);

        const $afc = this.createSelector(".feedback-container");
        const $afp = this.createSelector(feedbackPopupId);
        const $afm = this.createSelector(feedbackModalId);
        const $afe = this.createSelector(feedbackArticleEndId);

        const toggleContainer = this.createToggle($afc());
        const toggleModal = this.createToggle($afm());
        const togglePopup = this.createToggle($afp());

        const toggleOptions = this.createToggle($afc(".feedback-options"));
        const toggleOptionsSuccess = this.createToggle($afc(".feedback-options-success"));

        const toggleForm = this.createToggle($afc(".feedback-form"));
        const toggleFormValidationEmailError = this.createToggle($afc(".validation-error-email"));
        const toggleFormValidationMessageError = this.createToggle($afc(".validation-error-message"));
        const toggleFormServerError = this.createToggle($afc(".server-error"));

        // Only initalize the scroll handler if an end-of-article feedback section exists
        if ($afe().length) {
            $(window).on("scroll", throttle(() => {
                if (this.isInViewport($afe())) {
                    hidePopup("fast");
                } else if (!isDismissed && !$("body").hasClass("modal-open") && hasPopupBeenSeen) {
                    showPopup("slow");
                }
            }, 50))
        }

        const showModal = (speed = "fast") => {
            hidePopup();
            $("body").addClass("modal-open");
            $afm()
                .css({ opacity: 0, display: "flex" })
                .animate(
                    {
                        opacity: 1,
                    },
                    speed,
                    () => {
                        $afm(".feedback-form-message-input").focus()
                    }
                );
        };

        const hideModal = (speed = "fast") => {
            toggleModal(false, speed);
            $("body").removeClass("modal-open");
        };

        const showPopup = speed => {
            if (!this.isInViewport($afe())) {
                if (viewportWidth > MOBILE_BREAKPOINT) {
                    togglePopup(true, speed);
                } else {
                    $afp().addClass("show");
                }
            }
        }

        const hidePopup = speed => {
            if (viewportWidth > MOBILE_BREAKPOINT) {
                togglePopup(false, speed);
            } else {
                $afp().removeClass("show");
            }
        }

        const showThankYouMessage = (isModal, isCloseBtn) => {
            isDismissed = true;
            
            toggleOptions(false);
            toggleForm(false)
            toggleOptionsSuccess(true);

            if (isModal) {
                hideModal();

                // If the modal was activated from the popup, reshow the popup for the thank you
                if (viewportWidth <= MOBILE_BREAKPOINT && !this.isInViewport($afe()) && !isCloseBtn) {
                    showPopup();
                }
            }

            setTimeout(() => {
                toggleContainer(false, "fast");
            }, 2000)
        }

        const toggleSubmitButton = enabled => {
            const $button = $afc(".feedback-form-submit");
            $button
                .prop("disabled", !enabled)
                .toggleClass("disabled", !enabled);
        };

        $afc(".feedback-form-message-input").on("input", e => {
            toggleFormValidationMessageError(false);

            const message = $(e.currentTarget).val();
            if (this.validateMessage(message)) {
                $(e.currentTarget).addClass("valid");
                $(e.currentTarget).removeClass("invalid");
            } else {
                $(e.currentTarget).addClass("invalid");
                $(e.currentTarget).removeClass("valid");
        }
        });

        $afc(".feedback-form-email-input").on("input", e => {
            const email = $(e.currentTarget).val();
            if (!email || !email.length) {
                $(e.currentTarget).removeClass(["valid", "invalid"]);
                toggleFormValidationEmailError(false);
                return;
            }

            if (this.validateEmail(email)) {
                $(e.currentTarget).addClass("valid");
                $(e.currentTarget).removeClass("invalid");
                toggleFormValidationEmailError(false);
            } else {
                $(e.currentTarget).addClass("invalid");
                $(e.currentTarget).removeClass("valid");
                toggleFormValidationEmailError(true);
            }
        });

        // Event handler to close the modal by clicking outside of the modal window
        $afm().on("click", e => {
            const $target = $(e.target);
            if ($target.hasClass("feedback-modal")) {
                showThankYouMessage(true);
            }
        });

        // Event handler to close the modal by pressing ESCAPE
        $(window).on("keyup", e => {
            if (e.keyCode === 27) {
                showThankYouMessage(true);
            }
        });

        // Helpful Button Event Handler
        $afc(".feedback-btn.helpful").on("click", () => {
            this.trackFeedback("helpful");
            showThankYouMessage();
        });

        // Not-Helpful Button Event Handler
        $afc(".feedback-btn.not-helpful").on("click", e => {
            const containerType = this.getContainerType($(e.currentTarget));
            const isPopup = containerType === "popup";
            const isArticleEnd = containerType === "article-end";

            this.trackFeedback("not-helpful")

            if (isArticleEnd) {
                toggleForm(true);
                showModal();
            } else if (isPopup) {
                if (viewportWidth <= MOBILE_BREAKPOINT) {
                    hidePopup("fast")

                    // Let the popup finish animating out before opening the modal
                    setTimeout(() => {
                        toggleOptions(false);
                        toggleForm(true);
                        showModal()
                    }, POPUP_TRANSITION + 50);
                    ;
                } else {
                    toggleOptions(false);
                    toggleForm(true);

                    // Let the form finish appearing before attempting to focus 
                    setTimeout(() =>
                        $afp(".feedback-form-message-input").focus(),
                        50
                    );
                }
            }
        });

        // Event handler to close both the modal and the popup
        $afc(".feedback-close-btn").on("click", e => {
            const containerType = this.getContainerType($(e.currentTarget));
            const isModal = containerType === "modal";
            const isPopup = containerType === "popup";

            isDismissed = true;

            if (isModal) {
                this.trackFeedback("close-feedback-form");
                showThankYouMessage(isModal, true);
            } else if (isPopup) {
                if ($afp(".feedback-form:visible").length) {
                    this.trackFeedback("close-feedback-form");
                    showThankYouMessage();
                } else {
                    this.trackFeedback("close-feedback-popup");
                    hidePopup();
                }
            }
        });

        $afc(".feedback-form-submit").on("click", e => {
            e.preventDefault();

            toggleSubmitButton(false);
            toggleFormValidationMessageError(false);
            toggleFormValidationEmailError(false);
            toggleFormServerError(false);

            const containerType = this.getContainerType($(e.currentTarget));
            const isModal = containerType === "modal";

            const fieldSelectors = {
                modal: $afm,
                popup: $afp,
            };

            const feedbackEmail = fieldSelectors[containerType](".feedback-form-email-input").val();
            const feedbackMessage = fieldSelectors[containerType](".feedback-form-message-input").val();

            const isEmailValid = this.validateEmail(feedbackEmail);
            const isMessageValid = this.validateMessage(feedbackMessage);

            if (!(isMessageValid && isEmailValid)) {
                toggleSubmitButton(true);

                if (!isMessageValid) {
                    toggleFormValidationMessageError(true);
                }

                if (!isEmailValid) {
                    toggleFormValidationEmailError(true);
                }
            } else {
                $.ajax({
                    url: FORM_ENDPOINT,
                    type: "POST",
                    data: JSON.stringify({
                        feedback: feedbackMessage,
                        email: feedbackEmail,
                        url: window.location.href,
                    }),
                    dataType: "json",
                    contentType: "application/json",
                    success: () => {
                        toggleSubmitButton(true);
                        showThankYouMessage(isModal);
                        this.trackFeedback("submit-feedback");
                    },
                    error: () => {
                        toggleSubmitButton(true);
                        toggleFormServerError(true);
                    },
                });
            }
        });

        // Wait to show feedback options until they've had a chance to read something
        window.setTimeout(() => {
            if (!isDismissed) {
                hasPopupBeenSeen = true;
                showPopup("slow");
            }
        }, POPUP_APPEAR_TIMEOUT);
    }
}



// WEBPACK FOOTER //
// ./themes/docs/static/js/article-feedback.js